import 'ag-grid-enterprise/styles/ag-grid.css';
import 'ag-grid-enterprise/styles/ag-theme-quartz.css';
import { Box } from '@mui/joy';
import { SxProps } from '@mui/joy/styles/types';
import {
  AllEnterpriseModule,
  LicenseManager,
  ModuleRegistry,
  provideGlobalGridOptions,
} from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import { round } from 'lodash';
import { Dispatch, ReactElement, SetStateAction, useEffect, useState } from 'react';

import { LinearProgressWithValue } from 'core/elements';
import { useAgGridClientTableContext } from 'core/states';
import {
  ISelectionAction,
  ISelectionActionProps,
  ITableAction,
  ITableActionProps,
} from 'core/types';

import { EmptyStateWrapper } from './elements';
import {
  emptyStateOverlayStyle,
  GridHost,
  gridSlotStyle,
  hiddenGridSlotStyle,
  progressBarStyle as defaultProgressBarStyle,
  Wrapper,
} from './style';
import TopActions from './TopActions';
import './style.css';

ModuleRegistry.registerModules([AllEnterpriseModule]);

LicenseManager.setLicenseKey(process.env.REACT_APP_AG_GRID_KEY);

provideGlobalGridOptions({ theme: 'legacy' });

interface IProps {
  actions?: () => ITableAction[];
  selectionActions?: () => ISelectionAction[];
  closeSelection?: boolean;
  setCloseSelection?: Dispatch<SetStateAction<boolean>>;
  progressBarStyle?: SxProps;
  className?: string;
  actionButtons?: ReactElement[] | ((props: ITableActionProps) => ReactElement[]);
  rightActionButtons?: ReactElement[];
  selectionActionButtons?: ReactElement[] | ((props: ISelectionActionProps) => ReactElement[]);
  emptyStateElement?: ReactElement;
  hideTopActionsWhenEmpty?: boolean;
}

const AgGridTableView = ({
  actions,
  selectionActions,
  closeSelection = false,
  setCloseSelection,
  progressBarStyle = {},
  className = '',
  actionButtons = [],
  rightActionButtons = [],
  selectionActionButtons = [],
  emptyStateElement,
  hideTopActionsWhenEmpty = false,
}: IProps) => {
  const {
    tableId,
    gridRef,
    gridApi,
    gridOptions,
    rows,
    selectedRows,
    deselectAllRows,
    isDataLoading,
    isDataLoadCompleted,
    layoutType,
    hasLayoutActions,
    hasManageFilters,
    layoutChanged,
    resetMenuItems,
    onSaveLayout,
    loadingPercentage,
    rowGroupColumns,
    entityType,
    hasQuickSearch,
    hasTopActions,
    hasGroupByEnabled,
  } = useAgGridClientTableContext();

  const [shouldShowEmptyState, setShouldShowEmptyState] = useState(false);

  // This is a workaround to avoid showing the empty state when the table is loaded
  // but the data is still an empty array
  useEffect(() => {
    const timer = setTimeout(() => {
      setShouldShowEmptyState(
        !isDataLoading && !!isDataLoadCompleted && !rows.length && !!emptyStateElement
      );
    }, 250);

    return () => clearTimeout(timer);
  }, [isDataLoading, isDataLoadCompleted, rows.length, emptyStateElement]);

  // A grid showing its empty state can leave the page to it, toolbar included. Keyed to the
  // empty state rather than to the rows, so the toolbar stays through loading.
  const isTopActionsHidden = hideTopActionsWhenEmpty && shouldShowEmptyState;

  return (
    <Wrapper id={tableId} className={['ag-theme-quartz', className].join(' ')}>
      {hasTopActions && !isTopActionsHidden && (
        <TopActions
          hasQuickSearch={hasQuickSearch}
          actions={actions}
          actionButtons={actionButtons}
          rightActionButtons={rightActionButtons}
          selectionActions={selectionActions}
          selectionActionButtons={selectionActionButtons}
          selectionType={entityType}
          closeSelection={closeSelection}
          setCloseSelection={setCloseSelection}
          tableId={tableId}
          gridApi={gridApi}
          selectedRows={selectedRows}
          deselectAllRows={deselectAllRows}
          isDataLoading={isDataLoading}
          isDataLoadCompleted={isDataLoadCompleted}
          layoutType={layoutType}
          hasLayoutActions={hasLayoutActions}
          hasManageFilters={hasManageFilters}
          resetMenuItems={resetMenuItems}
          saveLayoutDisabled={!layoutChanged || rowGroupColumns.length > 0}
          onSaveLayout={onSaveLayout}
          hasGroupByEnabled={hasGroupByEnabled}
        />
      )}
      <GridHost>
        {shouldShowEmptyState && (
          <EmptyStateWrapper sx={emptyStateOverlayStyle}>{emptyStateElement}</EmptyStateWrapper>
        )}

        <Box sx={shouldShowEmptyState ? hiddenGridSlotStyle : gridSlotStyle}>
          <AgGridReact ref={gridRef} {...gridOptions} />
        </Box>
      </GridHost>
      <LinearProgressWithValue
        key={loadingPercentage}
        value={round(loadingPercentage)}
        sx={{ ...defaultProgressBarStyle, ...progressBarStyle }}
      />
    </Wrapper>
  );
};

export default AgGridTableView;
